import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton, Dialog, DialogTitle, DialogContent, Typography,
  DialogActions, Button, Snackbar, Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import { gql, useMutation } from '@apollo/client';
import getHelpfulErrorMessage from 'utils/getHelpfulErrorMessage';

const REVISE_OPEN_INVOICE = gql`
  mutation ReviseOpenInvoice($input: ReviseOpenInvoiceInput!) {
    reviseOpenInvoice(input: $input)
  }
`;
const ReviseButton = ({
  invoiceId,
  accountId,
  onRevise,
}) => {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasRevised, setHasRevised] = useState(false);
  const [error, setError] = useState(null);

  const [reviseOpenInvoice, { loading: isRevising }] = useMutation(REVISE_OPEN_INVOICE);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleRegenerateClick = async () => {
    try {
      await reviseOpenInvoice({
        variables: {
          input: {
            invoice_id: invoiceId,
            account_id: accountId,
          },
        },
      });

      setHasRevised(true);
      setIsDialogOpen(false);

      // Tell the parent to reload the invoice list
      onRevise();

    } catch (err) {
      setError(getHelpfulErrorMessage(err));
    }
  };

  const handleCloseSnackbar = () => {
    setHasRevised(false);
  };

  return (
    <>
      <Tooltip title="Regenerate invoice" placement="top">
        <IconButton onClick={handleOpenDialog}><RefreshIcon /></IconButton>
      </Tooltip>
      <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Regenerate invoice?</DialogTitle>
        <DialogContent>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          <Typography variant="subtitle1">
            This voids the existing invoice, and creates a new one with updated customer details, tax status, due date, and PO number.
          </Typography>

          <Typography variant="subtitle1">
            {'The new invoice will '}
            <strong>immediately be sent to the customer</strong>
            {' (via email).'}
          </Typography>

          <Typography variant="subtitle1">
            If this invoice has any "applied balance", it will also get applied on the new invoice, plus any credit that's already on the customer balance.
          </Typography>

        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={isRevising}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerateClick}
            variant="contained"
            color="primary"
            disabled={isRevising}
          >
            {isRevising ? 'Revising...' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={hasRevised}
        autoHideDuration={6000}
        message="Invoice regenerated"
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

ReviseButton.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  accountId: PropTypes.number.isRequired,
  onRevise: PropTypes.func.isRequired,
};

export default ReviseButton;
